"use client"

import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { FIELD_MONO, FIELD_UNDERLINE, FieldShell, Segmented } from "@/components/graphite/field"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/**
 * The fields a task is made of, shared by the composer and the editor.
 *
 * They had been written out twice — the same title, description, priority and
 * due date in two files, already drifting: the editor's description carried a
 * `min-h-[80px]` the composer's did not, and only one of them wrapped its date
 * picker in a `Controller`. Two spellings of one form is how a product ends up
 * with two answers to "what is a task".
 *
 * Priority is `Segmented` rather than a select. It is three mutually exclusive
 * words, which is what 15a's segmented control is for, and a dropdown would
 * hide two of the three behind a click for nothing.
 */

export const TASK_PRIORITIES = ["low", "medium", "high"] as const
export type TaskPriority = (typeof TASK_PRIORITIES)[number]

export function TaskTitleField({
  field,
  autoFocus,
}: {
  field: Record<string, unknown>
  autoFocus?: boolean
}) {
  return (
    <FieldShell label="Task title">
      <input
        {...field}
        autoFocus={autoFocus}
        placeholder="Enter task title..."
        aria-label="Task title"
        className={cn(FIELD_UNDERLINE, "pb-[7px] text-[13px] leading-[1.4]")}
      />
    </FieldShell>
  )
}

export function TaskDescriptionField({ field }: { field: Record<string, unknown> }) {
  return (
    <FieldShell label="Description" className="mt-5">
      <textarea
        {...field}
        rows={3}
        placeholder="Describe the task..."
        aria-label="Description"
        className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
      />
    </FieldShell>
  )
}

export function TaskPriorityField({
  value,
  onChange,
}: {
  value: TaskPriority
  onChange: (value: TaskPriority) => void
}) {
  return (
    <Segmented
      label="Priority"
      options={TASK_PRIORITIES}
      value={value}
      onChange={onChange}
      className="mt-5 flex-1"
    />
  )
}

export function TaskDueDateField({
  value,
  onChange,
  open,
  onOpenChange,
}: {
  value?: Date
  onChange: (date?: Date) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <FieldShell label="Due" className="mt-5 flex-1">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            /* Its only text is "—" until a date is chosen, which names nothing. */
            aria-label={value ? `Due date, ${format(value, "EEEE d MMMM yyyy")}` : "Due date"}
            className={cn(
              FIELD_UNDERLINE,
              FIELD_MONO,
              "flex items-center gap-2 pb-[7px] text-left",
              !value && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="size-3.5 flex-none" />
            {/* An em dash, not "Pick a due date": the label already says Due,
                and a task with no date is a fact rather than a prompt. */}
            {value ? format(value, "EEE d MMM yyyy") : "—"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              onChange(date)
              onOpenChange(false)
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </FieldShell>
  )
}
